import { useCallback, useEffect, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Modal from 'react-modal';
import backgroundImage from '../../images/background2.png'; // Nueva imagen de fondo
import { validarInputExpresionRegular } from "../../utils/generalFunctions";
import BotonSubmitPayU from "../PayU/BotonSubmitPayU";
import { alertaGeneral } from "../../utils/alertas";
import apiClient from "../../utils/axiosPetition";
import BotonSubmitEpayco from "../ePayco/BotonSubmitEpayco";
import { APP_URLS } from "../../config/appUrls";
import {
    fetchProfessionsGrouped,
    getServiceLabelFromGrouped,
    FALLBACK_PROFESSIONS_GROUPED,
} from "../../utils/professionService";
import { toSentenceCase } from "../../utils/formatServiceName";

Modal.setAppElement('#root');


export const Registro = () => {

    // No borrar localStorage aquí porque puede estar en uso para el flujo de pago
    // Solo se borrará después de un registro exitoso

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    
    // Determinar el tipo de registro desde URL params, pathname o state
    const tipoRegistroFromURL = searchParams.get('tipo');
    const isGratuitoFromURL = searchParams.get('gratuito') === 'true';
    
    // Detectar tipo de registro basado en la ruta actual
    const currentPath = location.pathname;
    const isGratuito = currentPath === '/registro-gratuito' || currentPath === '/registro-gratuito-usuario' || isGratuitoFromURL;
    const isPagado = currentPath === '/registro-pagado' || currentPath === '/registro-pagado-usuario';
    
    const [tipoRegistro] = useState(() => {
        // Si viene del state con tipoRegistro específico, usarlo
        if (location.state?.tipoRegistro) {
            return location.state.tipoRegistro;
        }
        
        // Si viene de URL params con tipo específico, usarlo
        if (tipoRegistroFromURL) {
            return tipoRegistroFromURL;
        }
        
        // Detectar tipo basado en la ruta
        if (currentPath === '/registro-gratuito-usuario' || currentPath === '/registro-pagado-usuario') {
            return 'usuario final';
        }
        
        // Para las rutas de prestadores
        if (currentPath === '/registro-gratuito' || currentPath === '/registro-pagado') {
            return 'prestador de servicios';
        }
        
        return 'prestador de servicios';
    });
    
    const [preInfo] = useState(location.state?.data);
    const color = tipoRegistro === 'usuario final' ? "#f79865ff" : "#1f94c2ff";


    const [data, setData] = useState(null);
    const [error, setError] = useState(true);
    const [precioMembresiaPaciente, setPrecioMembresiaPaciente] = useState('');
    const [precioLoadError, setPrecioLoadError] = useState(false);
    const [precioLoading, setPrecioLoading] = useState(true);
    // Precios membresía prestador: mensual, semestral, anual (desde BD; solo admin puede editarlos en Gestión de Precios)
    const [preciosMembresiaPrestador, setPreciosMembresiaPrestador] = useState({ mensual: null, semestral: null, anual: null });
    const [planMembresiaPrestador, setPlanMembresiaPrestador] = useState('mensual');
    const [preciosPrestadorUsandoFallback, setPreciosPrestadorUsandoFallback] = useState(false);
    const [preciosPrestadorCargando, setPreciosPrestadorCargando] = useState(true);
    const [isRegistering, setIsRegistering] = useState(false);

    // No usar valores por defecto: los precios deben venir siempre de la base de datos (API)
    // Si viene de OAuth, mostrar directamente el formulario de email
    const [showEmailForm, setShowEmailForm] = useState(location.state?.fromOAuth || location.state?.fromOAuthPaidUser || false); // Si viene de OAuth (gratuito o pagado), mostrar formulario
    // En /registro-pagado mostrar solo formulario de correo y pago Epayco (sin modal Google/LinkedIn)
    const [showEmailFormPrestador, setShowEmailFormPrestador] = useState(Boolean(currentPath === '/registro-pagado') || Boolean(isPagado && location.state?.fromOAuthDoctor && location.state?.needsPayment));
    const [showAuthModal, setShowAuthModal] = useState(false); // Modal para aceptar autorización antes de OAuth
    const [pendingOAuthProvider, setPendingOAuthProvider] = useState(null); // Guardar el proveedor OAuth pendiente
    const [aceptoAutorizacionOAuth, setAceptoAutorizacionOAuth] = useState(false); // Estado del checkbox en el modal
    // Especialidad para registro de prestador por Google/LinkedIn (obligatoria antes de OAuth)
    const [oauthPrestadorSpecialty, setOauthPrestadorSpecialty] = useState('');
    const [showServiceModalPrestador, setShowServiceModalPrestador] = useState(false);
    const [especialidadesPrestador, setEspecialidadesPrestador] = useState(FALLBACK_PROFESSIONS_GROUPED);
    const [pantallaActual, setPantallaActual] = useState(() => {
        // Para usuarios finales, no mostrar pantalla de selección
        if (tipoRegistro === 'usuario final') {
            return isGratuito ? 'gratuito' : 'membresia';
        }
        return 'seleccion'; // Para prestadores de servicios
    }); // 'seleccion', 'gratuito', 'membresia'

    useEffect(() => {
        fetchProfessionsGrouped().then(setEspecialidadesPrestador);
    }, []);

    const onHandleSubmit = (e) => {
        e.preventDefault();
        console.log('onHandleSubmit llamado:', { tipoRegistro, isGratuito, data });
        if (tipoRegistro === 'usuario final' && isGratuito) {
            registroPaciente();
        } else if (tipoRegistro === 'prestador de servicios' && isGratuito) {
            registroAfiliadoGratuito();
        }
    }

    const getServiceLabelPrestador = (value) =>
        getServiceLabelFromGrouped(especialidadesPrestador, value);

    const handleSocialLogin = async (provider) => {
        // Para prestador: primero guardar proveedor y mostrar modal de especialidad; después el de autorización
        if (tipoRegistro === 'prestador de servicios') {
            setPendingOAuthProvider(provider);
            setShowServiceModalPrestador(true);
            return;
        }
        setPendingOAuthProvider(provider);
        setShowAuthModal(true);
    };

    // Al elegir especialidad (prestador OAuth): cerrar modal de especialidad y abrir el de autorización
    const handlePrestadorSpecialtySelected = (serviceValue) => {
        setOauthPrestadorSpecialty(serviceValue);
        setShowServiceModalPrestador(false);
        setShowAuthModal(true);
    };

    const handleConfirmOAuth = async () => {
        if (!aceptoAutorizacionOAuth) {
            alertaGeneral({
                titulo: 'Autorización requerida',
                messageHtml: 'Debes aceptar la autorización para el tratamiento de datos personales para continuar.',
                isWarning: true
            });
            return;
        }

        // Cerrar modal y proceder con OAuth
        setShowAuthModal(false);
        const provider = pendingOAuthProvider;
        setPendingOAuthProvider(null);
        setAceptoAutorizacionOAuth(false);

        try {
            const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
            const flow = tipoRegistro === 'prestador de servicios' ? 'register_doctor' : 'register';
            const returnPath = (tipoRegistro === 'prestador de servicios' && currentPath === '/registro-pagado') ? 'registro-pagado' : '';
            const oauthUrl = returnPath ? `${baseUrl}/api/auth/${provider}?flow=${flow}&return_path=${encodeURIComponent(returnPath)}` : `${baseUrl}/api/auth/${provider}?flow=${flow}`;

            // Guardar datos previos al OAuth (especialidad para doctor y celular para notificación WhatsApp)
            try {
                await apiClient.post(
                    '/api/auth/prepare-doctor-oauth',
                    {
                        specialty: tipoRegistro === 'prestador de servicios' ? oauthPrestadorSpecialty : '',
                        cellNumber: data?.cellNumber || ''
                    },
                    { withCredentials: true }
                );
            } catch (err) {
                console.error('Error preparando OAuth:', err);
                alertaGeneral({
                    titulo: 'Error',
                    messageHtml: 'No se pudo preparar el registro. Intenta de nuevo o regístrate con correo.',
                    isError: true
                });
                return;
            }

            sessionStorage.setItem('oauth_aceptoAutorizacion', 'true');
            if (tipoRegistro === 'prestador de servicios') {
                if (currentPath === '/registro-pagado') {
                    sessionStorage.setItem('oauth_return_path', '/registro-pagado');
                } else {
                    sessionStorage.removeItem('oauth_return_path');
                }
            } else if (currentPath === '/registro-pagado-usuario') {
                sessionStorage.setItem('oauth_return_path', '/registro-pagado-usuario');
            } else {
                sessionStorage.removeItem('oauth_return_path');
            }

            window.location.href = oauthUrl;
        } catch (error) {
            console.error('Error en handleSocialLogin:', error);
            alertaGeneral({
                titulo: 'Error',
                messageHtml: `Error al iniciar el registro con ${provider === 'google' ? 'Google' : 'LinkedIn'}. Por favor, intenta con el formulario de correo.`,
                isError: true
            });
        }
    };

    // Verificar si hay errores de OAuth en la URL
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        
        if (error === 'oauth_not_configured') {
            alertaGeneral({
                titulo: 'OAuth no configurado',
                messageHtml: 'El registro con redes sociales requiere configuración adicional. Por favor, usa el formulario de correo para registrarte.',
                isWarning: true
            });
            // Limpiar el parámetro de la URL
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (error === 'oauth_failed') {
            alertaGeneral({
                titulo: 'Error en autenticación',
                messageHtml: 'Hubo un problema con la autenticación. Por favor, intenta nuevamente o usa el formulario de correo.',
                isError: true
            });
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    const registroPaciente = async () => {
        try {
            setIsRegistering(true);
            
            // Validar contraseña antes de enviar
            if (!data.password || data.password.length < 8) {
                alertaGeneral({
                    titulo: 'Error',
                    messageHtml: 'La contraseña debe tener al menos 8 caracteres',
                    isError: true
                });
                return;
            }

            if (data.password !== data.confirmPassword) {
                alertaGeneral({
                    titulo: 'Error',
                    messageHtml: 'Las contraseñas no coinciden',
                    isError: true
                });
                return;
            }
            
            // Normalizar email a minúsculas
            const normalizedData = { 
                ...data, 
                email: data.email.toLowerCase().trim(),
                password: data.password // Incluir la contraseña en los datos
            };
            
            // Validar si el email ya existe
            const emailExists = await validateEmailPaciente();
            if (emailExists) {
                return;
            }

            // Registrar el paciente con contraseña
            const response = await apiClient.post(`/api/patientRegister/register`, normalizedData);
            
            if (response.status === 201) {
                alertaGeneral({
                    titulo: 'Registro Exitoso',
                    messageHtml: 'Tu cuenta ha sido creada exitosamente. Ya puedes iniciar sesión.',
                });
                navigate('/login');
            }
        } catch (error) {
            alertaGeneral({
                titulo: 'Error',
                messageHtml: error?.response?.data?.message || 'Ocurrió un error al registrar el usuario final',
                isError: true
            });
        } finally {
            setIsRegistering(false);
        }
    }

    const registroAfiliadoGratuito = async () => {
        console.log('registroAfiliadoGratuito llamado con data:', data);
        try {
            setIsRegistering(true);
            
            // Validar que data existe
            if (!data || !data.email) {
                console.warn('Data incompleta:', data);
                alertaGeneral({
                    titulo: 'Error',
                    messageHtml: 'Por favor completa todos los campos del formulario',
                    isError: true
                });
                return;
            }
            
            // Normalizar email a minúsculas
            const normalizedData = { ...data, email: data.email.toLowerCase().trim() };
            
            // Validar si el email ya existe
            const emailExists = await validateEmailAfiliado();
            if (emailExists) {
                return;
            }

            // Registrar el afiliado sin pago (el backend también lo registra como paciente automáticamente)
            const response = await apiClient.post('/api/afiliado/registro-gratuito', normalizedData);
            
            if (response.status === 201) {
                alertaGeneral({
                    titulo: 'Registro Exitoso',
                    messageHtml: 'Te has registrado exitosamente como profesional.'
                });
                
                // Redirigir al login después del registro exitoso
                navigate('/login');
            }
        } catch (error) {
            console.error('Error en registroAfiliadoGratuito:', error);
            alertaGeneral({
                titulo: 'Error',
                messageHtml: error?.response?.data?.message || error?.message || 'Ocurrió un error al registrar el prestador de servicios',
                isError: true
            });
        } finally {
            setIsRegistering(false);
        }
    }

    const validateEmailAfiliado = async () => {
        try {
            if (!data || !data.email) {
                return false;
            }
            const res = await apiClient.get(`/api/afiliado/validar-email/${data.email}`);
            if (res.data.isExists) {
                alertaGeneral({
                    titulo: 'Correo ya registrado',
                    messageHtml: res.data.message || 'El correo ya está registrado. Si ya tienes cuenta profesional, inicia sesión para usar tu perfil de paciente en la misma cuenta.',
                    isError: true,
                });
                setIsRegistering(false); // Detener el loading si hay error
                return true; // Indica que el correo ya existe
            }
            return false; // El correo no está registrado
        } catch (error) {
            alertaGeneral({
                titulo: 'Error',
                messageHtml: error?.response?.data?.message || 'Ocurrió un error al validar el correo',
                isError: true,
            });
            setIsRegistering(false); // Detener el loading si hay error
            return true; // Asume error como un caso inválido
        }
    };

    const validateCedulaAfiliado = async () => {
        try {
            if (!data || !data.idNumber) {
                return false;
            }
            const res = await apiClient.get(`/api/afiliado/validar-cedula/${data.idNumber}`);
            if (res.data.isExists) {
                alertaGeneral({
                    titulo: 'Cédula ya registrada',
                    messageHtml: res.data.message || 'La cédula ya está registrada. Si ya tienes cuenta, inicia sesión para continuar.',
                    isError: true,
                });
                setIsRegistering(false);
                return true; // Indica que la cédula ya existe
            }
            return false; // La cédula no está registrada
        } catch (error) {
            alertaGeneral({
                titulo: 'Error',
                messageHtml: error?.response?.data?.message || 'Ocurrió un error al validar la cédula',
                isError: true,
            });
            setIsRegistering(false);
            return true;
        }
    };

    const validateAllUniqueFields = async () => {
        if (!data) {
            return true; // Si no hay data, no validar
        }
        
        // Validar email
        const emailExists = await validateEmailAfiliado();
        if (emailExists) {
            return true; // Hay error, detener
        }
        
        // Validar cédula
        if (data.idNumber) {
            const cedulaExists = await validateCedulaAfiliado();
            if (cedulaExists) {
                return true; // Hay error, detener
            }
        }
        
        return false; // No hay errores
    };

    const validateEmailPaciente = async () => {
        try {
            const res = await apiClient.get(`/api/patientRegister/validar-email/${data.email}`);
            if (res.data.isExists) {
                alertaGeneral({
                    titulo: 'Correo ya registrado',
                    messageHtml: res.data.message || 'El correo ya está registrado. Si ya tienes cuenta profesional, inicia sesión para usar tu perfil de paciente en la misma cuenta.',
                    isError: true,
                });
                setIsRegistering(false);
                return true;
            }
            return false;
        } catch (error) {
            alertaGeneral({
                titulo: 'Error',
                messageHtml: error?.response?.data?.message || 'Ocurrió un error al validar el correo',
                isError: true,
            });
            setIsRegistering(false);
            return true;
        }
    };



    const fetchPrecios = useCallback(() => {
        setPrecioLoading(true);
        setPrecioLoadError(false);
        apiClient.get('/api/precios')
            .then(res => {
                const precioPaciente = res.data?.find(s => s.nombre === 'Membresía Paciente Premium');
                setPrecioMembresiaPaciente(precioPaciente != null && typeof precioPaciente.valor === 'number' ? Number(precioPaciente.valor) : '');
                setPrecioLoadError(false);
            })
            .catch(err => {
                console.warn('Error al obtener precios:', err?.response?.data?.message || err.message);
                setPrecioMembresiaPaciente('');
                setPrecioLoadError(true);
            })
            .finally(() => setPrecioLoading(false));
    }, []);

    const needsPrecioPacientePremium =
        currentPath === '/registro-pagado-usuario' || currentPath === '/registro-gratuito-usuario';

    useEffect(() => {
        if (!needsPrecioPacientePremium) {
            setPrecioLoading(false);
            return;
        }
        fetchPrecios();
    }, [fetchPrecios, needsPrecioPacientePremium]);

    const toValorMembresiaPrestador = (v) =>
        (v != null && typeof v === 'object' && 'valor' in v ? v.valor : typeof v === 'number' ? v : null);

    // Precios solo desde la base de datos; no usar fallbacks
    const fetchPreciosMembresiaPrestador = useCallback(() => {
        setPreciosPrestadorCargando(true);
        setPreciosPrestadorUsandoFallback(false);

        apiClient.get('/api/membresia-prestador-precios', { timeout: 45000 })
            .then(res => {
                const data = res?.data;
                if (!data) {
                    setPreciosMembresiaPrestador({ mensual: null, semestral: null, anual: null });
                    setPreciosPrestadorUsandoFallback(true);
                    return;
                }
                const mensual = toValorMembresiaPrestador(data.mensual);
                const semestral = toValorMembresiaPrestador(data.semestral);
                const anual = toValorMembresiaPrestador(data.anual);
                setPreciosMembresiaPrestador({ mensual, semestral, anual });
                setPreciosPrestadorUsandoFallback(mensual == null && semestral == null && anual == null);
            })
            .catch(err => {
                const msg = err?.response?.data?.message || err.message;
                console.warn('Error al obtener precios membresía prestador:', msg);
                setPreciosMembresiaPrestador({ mensual: null, semestral: null, anual: null });
                setPreciosPrestadorUsandoFallback(true);
            })
            .finally(() => setPreciosPrestadorCargando(false));
    }, []);

    // Precios membresía prestador (mensual, semestral, anual) para registro-pagado
    useEffect(() => {
        const isPrestadorPagado = currentPath === '/registro-pagado';
        if (!isPrestadorPagado) return undefined;

        let ignore = false;
        setPreciosPrestadorCargando(true);
        setPreciosPrestadorUsandoFallback(false);

        apiClient.get('/api/membresia-prestador-precios', { timeout: 45000 })
            .then(res => {
                if (ignore) return;
                const data = res?.data;
                if (!data) {
                    setPreciosMembresiaPrestador({ mensual: null, semestral: null, anual: null });
                    setPreciosPrestadorUsandoFallback(true);
                    return;
                }
                const mensual = toValorMembresiaPrestador(data.mensual);
                const semestral = toValorMembresiaPrestador(data.semestral);
                const anual = toValorMembresiaPrestador(data.anual);
                setPreciosMembresiaPrestador({ mensual, semestral, anual });
                setPreciosPrestadorUsandoFallback(mensual == null && semestral == null && anual == null);
            })
            .catch(err => {
                if (ignore) return;
                console.warn('Error al obtener precios membresía prestador:', err?.response?.data?.message || err.message);
                setPreciosMembresiaPrestador({ mensual: null, semestral: null, anual: null });
                setPreciosPrestadorUsandoFallback(true);
            })
            .finally(() => {
                if (!ignore) setPreciosPrestadorCargando(false);
            });

        return () => { ignore = true; };
    }, [currentPath]);

    const isRegistroPagadoPrestador = currentPath === '/registro-pagado';
    const isRegistroGratuitoPrestador = currentPath === '/registro-gratuito';
    const isRegistroPagadoUsuario = currentPath === '/registro-pagado-usuario';
    const isRegistroGratuitoUsuario = currentPath === '/registro-gratuito-usuario';
    const isStyledRegistroPrestador = isRegistroPagadoPrestador || isRegistroGratuitoPrestador;
    const isStyledRegistroUsuario = isRegistroPagadoUsuario || isRegistroGratuitoUsuario;
    const isStyledRegistro = isStyledRegistroPrestador || isStyledRegistroUsuario;

    return (
        <div
            className="flex items-center justify-center min-h-screen bg-cover bg-center"
            style={{ backgroundImage: `url(${backgroundImage})` }}
        >
            <div className="absolute inset-0 bg-white bg-opacity-60" ></div>

            <div className={`relative w-full bg-white rounded-2xl sm:rounded-3xl border-2 shadow-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto ${isStyledRegistro ? 'max-w-[480px]' : 'max-w-[450px] border-[#6e787aff] py-4 sm:py-6'} ${(isRegistroPagadoPrestador || isRegistroPagadoUsuario) ? 'border-[#f79865]/40 sm:border-[#f79865]/60' : ''} ${(isRegistroGratuitoPrestador || isRegistroGratuitoUsuario) ? 'border-[#1f94c2]/40 sm:border-[#1f94c2]/60' : ''}`}>
                <div className={isStyledRegistro ? 'pt-5 sm:pt-6 pb-4 sm:pb-6' : ''}>
                <div className={`text-center mb-4 sm:mb-6 px-4 sm:px-6`}>
                    {isRegistroPagadoPrestador ? (
                        <>
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#fff5f0] border border-[#f79865]/40 text-[#205087] text-xs font-semibold mb-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#f79865]" />
                                Membresía para profesionales
                            </div>
                            <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mb-1">
                                Redplika <span className="text-[#f79865]">Premium</span>
                            </h3>
                            <p className="text-sm text-gray-600 font-medium max-w-[320px] mx-auto">
                                Accede a más beneficios y herramientas para tu práctica profesional.
                            </p>
                        </>
                    ) : isRegistroGratuitoPrestador ? (
                        <>
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#eff6ff] border border-[#1f94c2]/40 text-[#205087] text-xs font-semibold mb-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#1f94c2]" />
                                Plan gratuito
                            </div>
                            <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mb-1">
                                Registro como <span className="text-[#1f94c2]">Profesional</span>
                            </h3>
                            <p className="text-sm text-gray-600 font-medium max-w-[320px] mx-auto">
                                Únete sin costo y comienza a ofrecer tus servicios en la plataforma.
                            </p>
                        </>
                    ) : isRegistroGratuitoUsuario ? (
                        <>
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#eff6ff] border border-[#1f94c2]/40 text-[#205087] text-xs font-semibold mb-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#1f94c2]" />
                                Plan gratuito
                            </div>
                            <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mb-1">
                                Registro como <span className="text-[#1f94c2]">Paciente</span>
                            </h3>
                            <p className="text-sm text-gray-600 font-medium max-w-[320px] mx-auto">
                                Crea tu cuenta sin costo y accede a la plataforma.
                            </p>
                        </>
                    ) : isRegistroPagadoUsuario ? (
                        <>
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#fff5f0] border border-[#f79865]/40 text-[#205087] text-xs font-semibold mb-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#f79865]" />
                                Membresía para pacientes
                            </div>
                            <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mb-1">
                                Redplika <span className="text-[#f79865]">Premium</span>
                            </h3>
                            <p className="text-sm text-gray-600 font-medium max-w-[320px] mx-auto">
                                Accede a beneficios premium como paciente.
                            </p>
                        </>
                    ) : (
                        <>
                            <div className="mb-2 sm:mb-3">
                                <span className="text-3xl sm:text-4xl">🎉</span>
                            </div>
                            <h3 className={`text-lg sm:text-xl md:text-2xl font-black mb-1 sm:mb-2 ${tipoRegistro === 'prestador de servicios' && isPagado ? 'text-[#f79865]' : ''} ${tipoRegistro === 'usuario final' && isGratuito ? 'text-[#1f94c2ff]' : ''} ${tipoRegistro === 'usuario final' && isPagado ? 'text-[#f79865]' : ''}`} style={tipoRegistro === 'usuario final' ? undefined : (tipoRegistro === 'prestador de servicios' && isPagado ? undefined : { color })}>
                                {tipoRegistro === 'prestador de servicios' ? 'Registro como Profesional' : tipoRegistro === 'usuario final' ? 'Registro como Paciente' : `Registro como ${tipoRegistro}`}
                            </h3>
                            {isGratuito && (
                                <p className="text-xs sm:text-sm text-gray-600 font-medium">
                                    Plan Gratuito - Sin costo
                                </p>
                            )}
                        </>
                    )}
                </div>

                {/* Aviso cuando falla la carga de precios (registro pagado) */}
                {isPagado && precioLoadError && (
                    <div className="mx-4 mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <p className="text-sm text-amber-800 mb-2">
                            No se pudieron cargar los precios desde el servidor. Se muestran valores de referencia.
                        </p>
                        <button
                            type="button"
                            onClick={() => fetchPrecios()}
                            disabled={precioLoading}
                            className="text-sm font-semibold text-amber-800 hover:text-amber-900 underline disabled:opacity-50"
                        >
                            Reintentar
                        </button>
                    </div>
                )}

                {/* Formulario de registro gratuito para pacientes */}
                {tipoRegistro === 'usuario final' && isGratuito && (
                    <div className={isRegistroGratuitoUsuario ? 'px-4 sm:px-6 md:px-8' : 'px-4 sm:px-6 md:px-8'}>
                        <div className="flex items-center justify-between mb-4 sm:mb-6">
                            {!showEmailForm ? (
                                <button
                                    type="button"
                                    onClick={() => navigate('/login')}
                                    className="flex items-center space-x-1 text-[#1f94c2ff] text-xs sm:text-sm hover:text-[#3090b3ff] transition duration-200 font-medium"
                                >
                                    <span>←</span>
                                    <span>Volver</span>
                                </button>
                            ) : (
                                <div />
                            )}
                            {showEmailForm && (
                                <span className="text-xs sm:text-sm text-gray-500 font-medium">Paso 1 de 2</span>
                            )}
                            {!showEmailForm && <div />}
                        </div>
                        
                        {!showEmailForm ? (
                            <>
                                {/* Opciones de registro */}
                                <div className="mb-4 sm:mb-6 space-y-3">
                                    <button
                                        type="button"
                                        onClick={() => handleSocialLogin('google')}
                                        className="w-full flex items-center justify-center px-4 sm:px-5 py-3 sm:py-3.5 border-2 border-gray-200 rounded-xl bg-white text-gray-700 hover:border-[#4285F4] hover:bg-[#f8f9fa] hover:shadow-md transition-all duration-300 text-sm sm:text-base font-medium group"
                                    >
                                        <div className="flex items-center justify-center space-x-3">
                                            <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" viewBox="0 0 24 24">
                                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                            </svg>
                                            <span className="font-semibold">Continuar con Google</span>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleSocialLogin('linkedin')}
                                        className="w-full flex items-center justify-center px-4 sm:px-5 py-3 sm:py-3.5 border-2 border-gray-200 rounded-xl bg-white text-gray-700 hover:border-[#0077B5] hover:bg-[#f8f9fa] hover:shadow-md transition-all duration-300 text-sm sm:text-base font-medium group"
                                    >
                                        <div className="flex items-center justify-center space-x-3">
                                            <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" viewBox="0 0 24 24" fill="#0077B5">
                                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                            </svg>
                                            <span className="font-semibold">Continuar con LinkedIn</span>
                                        </div>
                                    </button>
                                </div>
                                
                                <div className="relative mb-4 sm:mb-6">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-200"></div>
                                    </div>
                                    <div className="relative flex justify-center">
                                        <span className="px-3 sm:px-4 bg-white text-gray-400 text-xs sm:text-sm font-medium">o</span>
                                    </div>
                                </div>
                                
                                <button
                                    type="button"
                                    onClick={() => setShowEmailForm(true)}
                                    className="w-full flex items-center justify-center px-4 sm:px-5 py-3 sm:py-3.5 bg-[#1f94c2ff] hover:bg-[#1a7fa8] border-2 border-[#1f94c2ff] rounded-xl text-white hover:shadow-md transition-all duration-300 font-semibold text-sm sm:text-base"
                                >
                                    <span className="mr-2 text-lg sm:text-xl">📧</span>
                                    <span>Registrarse con correo electrónico</span>
                                </button>
                            </>
                        ) : (
                            <form onSubmit={onHandleSubmit} className="space-y-4">
                                <button
                                    type="button"
                                    onClick={() => setShowEmailForm(false)}
                                    className="flex items-center space-x-1 text-[#1f94c2ff] text-xs sm:text-sm hover:text-[#3090b3ff] transition duration-200 font-medium mb-2"
                                >
                                    <span>←</span>
                                    <span>Volver a opciones</span>
                                </button>
                                
                                {isRegistroGratuitoUsuario ? (
                                    <div className="rounded-2xl bg-gray-50/80 border border-gray-100 p-4 sm:p-5 mb-5">
                                        <p className="text-xs font-medium text-gray-500 mb-4 ">Tus datos</p>
                                        <RegistroPaciente formVariant="gratuito" setData={setData} setError={setError} initialEmail={location.state?.emailFromOAuth || ''} />
                                    </div>
                                ) : (
                                    <RegistroPaciente setData={setData} setError={setError} initialEmail={location.state?.emailFromOAuth || ''} />
                                )}
                                
                                <button
                                    type="submit"
                                    className={`w-full text-sm sm:text-base font-bold focus:outline-none rounded-xl transition-all duration-300 flex items-center justify-center mt-4 ${
                                        isRegistroGratuitoUsuario ? 'px-4 py-3.5 border-2 border-[#1f94c2] shadow-md' : 'px-4 sm:px-6 py-3 sm:py-3.5 shadow-lg'
                                    } ${
                                        isRegistering 
                                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed shadow-none border-gray-400' 
                                            : 'bg-[#1f94c2ff] hover:bg-[#1a7fa8] text-white hover:shadow-xl transform hover:scale-[1.02]'
                                    }`}
                                    disabled={!data?.aceptoAutorizacion || !data?.password || !data?.confirmPassword || data?.password !== data?.confirmPassword || isRegistering}
                                >
                                    {isRegistering ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>Registrando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="mr-2 text-lg">✨</span>
                                            <span>Crear mi cuenta gratis</span>
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                )}

                {/* Formulario de registro con membresía para pacientes */}
                {tipoRegistro === 'usuario final' && isPagado && (
                    <div className={isRegistroPagadoUsuario ? 'px-4 sm:px-6 md:px-8' : 'px-6'}>
                        <div className="flex items-center mb-4">
                            <button
                                type="button"
                                onClick={() => navigate('/login')}
                                className="text-[#f79865] text-xs hover:underline mr-3"
                            >
                                ← Volver
                            </button>
                        </div>

                        <form onSubmit={onHandleSubmit}>
                            {isRegistroPagadoUsuario ? (
                                <div className="rounded-2xl bg-gray-50/80 border border-gray-100 p-4 sm:p-5 mb-5">
                                    <p className="text-xs font-medium text-gray-500 mb-4 ">Tus datos</p>
                                    <RegistroPaciente formVariant="premium" setData={setData} setError={setError} initialEmail={location.state?.emailFromOAuth || location.state?.patientEmail || ''} fromOAuthPaidUser={location.state?.fromOAuthPaidUser} />
                                </div>
                            ) : (
                                <RegistroPaciente setData={setData} setError={setError} initialEmail={location.state?.emailFromOAuth || location.state?.patientEmail || ''} fromOAuthPaidUser={location.state?.fromOAuthPaidUser} />
                            )}

                            <div className="text-center font-semibold text-[#205087ff] mb-4">
                                Valor de la membresía Premium: ${precioMembresiaPaciente}
                            </div>

                            <BotonSubmitEpayco
                                descripcion={'Pago Membresía Premium Paciente'}
                                correo={data?.email}
                                amount={precioMembresiaPaciente}
                                invoice={data?.email}
                                disabled={error || !precioMembresiaPaciente || !data?.aceptoAutorizacion || !data?.email || (!location.state?.fromOAuthPaidUser && (!data?.password || !data?.confirmPassword || data?.password !== data?.confirmPassword))}
                                disabledFunction={location.state?.fromOAuthPaidUser ? undefined : validateEmailPaciente}
                                redireccion={APP_URLS.registroPacientePremium}
                                doctorId={undefined}
                                registrationData={data}
                                extraData={{
                                    tipo: 'paciente_premium',
                                    firstName: data?.firstName,
                                    secondName: data?.secondName,
                                    firstLastName: data?.firstLastName,
                                    secondLastName: data?.secondLastName,
                                    email: data?.email,
                                    cellNumber: data?.cellNumber,
                                    x_extra1: 'membresia_premium',
                                    x_extra2: data?.email,
                                    x_extra3: `${data?.firstName} ${data?.firstLastName}`
                                }}
                            />
                        </form>
                    </div>
                )}

                {/* Registro prestadores: en /registro-pagado solo formulario correo + Epayco; en gratuito opción Google/LinkedIn o correo */}
                {tipoRegistro === 'prestador de servicios' && (
                    <>
                        {!showEmailFormPrestador ? (
                            <div className="px-4 sm:px-6 md:px-8">
                                <div className="flex items-center justify-between mb-4 sm:mb-6">
                                    <button
                                        type="button"
                                        onClick={() => navigate('/login')}
                                        className="flex items-center space-x-1 text-[#1f94c2ff] text-xs sm:text-sm hover:text-[#3090b3ff] transition duration-200 font-medium"
                                    >
                                        <span>←</span>
                                        <span>Volver</span>
                                    </button>
                                    <span className="text-xs sm:text-sm text-gray-500 font-medium">Paso 1 de 2</span>
                                </div>
                                <div className="mb-4 sm:mb-6 space-y-3">
                                    <button
                                        type="button"
                                        onClick={() => handleSocialLogin('google')}
                                        className="w-full flex items-center justify-center px-4 sm:px-5 py-3 sm:py-3.5 border-2 border-gray-200 rounded-xl bg-white text-gray-700 hover:border-[#4285F4] hover:bg-[#f8f9fa] hover:shadow-md transition-all duration-300 text-sm sm:text-base font-medium group"
                                    >
                                        <div className="flex items-center justify-center space-x-3">
                                            <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" viewBox="0 0 24 24">
                                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                            </svg>
                                            <span className="font-semibold">Continuar con Google</span>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleSocialLogin('linkedin')}
                                        className="w-full flex items-center justify-center px-4 sm:px-5 py-3 sm:py-3.5 border-2 border-gray-200 rounded-xl bg-white text-gray-700 hover:border-[#0077B5] hover:bg-[#f8f9fa] hover:shadow-md transition-all duration-300 text-sm sm:text-base font-medium group"
                                    >
                                        <div className="flex items-center justify-center space-x-3">
                                            <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" viewBox="0 0 24 24" fill="#0077B5">
                                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                            </svg>
                                            <span className="font-semibold">Continuar con LinkedIn</span>
                                        </div>
                                    </button>
                                </div>
                                <div className="relative mb-4 sm:mb-6">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-200"></div>
                                    </div>
                                    <div className="relative flex justify-center">
                                        <span className="px-3 sm:px-4 bg-white text-gray-400 text-xs sm:text-sm font-medium">o</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowEmailFormPrestador(true)}
                                    className="w-full flex items-center justify-center px-4 sm:px-5 py-3 sm:py-3.5 bg-[#1f94c2ff] hover:bg-[#1a7fa8] border-2 border-[#1f94c2ff] rounded-xl text-white hover:shadow-md transition-all duration-300 font-semibold text-sm sm:text-base"
                                >
                                    <span className="mr-2 text-lg sm:text-xl">📧</span>
                                    <span>Registrarse con correo electrónico</span>
                                </button>
                            </div>
                        ) : (
                    <form onSubmit={onHandleSubmit} className={isStyledRegistroPrestador ? 'px-4 sm:px-6 md:px-8 pb-6' : 'px-3 sm:px-6 md:px-9 pb-4'}>
                        {currentPath !== '/registro-pagado' && currentPath !== '/registro-gratuito' && (
                        <button
                            type="button"
                            onClick={() => setShowEmailFormPrestador(false)}
                            className="flex items-center space-x-1 text-[#1f94c2ff] text-xs sm:text-sm hover:text-[#3090b3ff] transition duration-200 font-medium mb-2"
                        >
                            <span>←</span>
                            <span>Volver a opciones</span>
                        </button>
                        )}
                        {isRegistroPagadoPrestador && (
                            <div className="rounded-2xl bg-gray-50/80 border border-gray-100 p-4 sm:p-5 mb-5">
                                <p className="text-xs font-medium text-gray-500 mb-4 ">Tus datos</p>
                                <RegistroAfiliado formVariant="premium" preInfo={isPagado && location.state?.fromOAuthDoctor && location.state?.needsPayment ? { fullName: location.state?.doctorName || '', email: location.state?.email || '', aceptoAutorizacion: true, ...location.state?.data } : preInfo} setData={setData} setError={setError} isPagado={isPagado} />
                            </div>
                        )}
                        {isRegistroGratuitoPrestador && (
                            <div className="rounded-2xl bg-gray-50/80 border border-gray-100 p-4 sm:p-5 mb-5">
                                <p className="text-xs font-medium text-gray-500 mb-4 ">Tus datos</p>
                                <RegistroAfiliado formVariant="gratuito" preInfo={preInfo} setData={setData} setError={setError} isPagado={false} />
                            </div>
                        )}
                        {!isStyledRegistroPrestador && <RegistroAfiliado preInfo={isPagado && location.state?.fromOAuthDoctor && location.state?.needsPayment ? { fullName: location.state?.doctorName || '', email: location.state?.email || '', aceptoAutorizacion: true, ...location.state?.data } : preInfo} setData={setData} setError={setError} isPagado={isPagado} />}

                        {tipoRegistro === 'prestador de servicios' && isGratuito && (
                            <button
                                type="submit"
                                className={`w-full font-semibold focus:outline-none flex items-center justify-center transition duration-300 ${
                                    isRegistroGratuitoPrestador ? 'px-4 py-3.5 rounded-xl border-2 border-[#1f94c2]' : 'px-2 py-1 rounded-lg border border-[#6e787aff]'
                                } ${
                                    isRegistering 
                                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed border-gray-400' 
                                        : 'bg-[#1f94c2ff] text-white hover:bg-[#1a7fa8] border-[#1f94c2] hover:shadow-md'
                                }`}
                                disabled={error || !data?.aceptoAutorizacion || isRegistering}
                            >
                                {isRegistering ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Registrando...
                                    </>
                                ) : (
                                    'Registrarse Gratis'
                                )}
                            </button>
                        )}

                        {tipoRegistro === 'prestador de servicios' && !isGratuito && (
                            <>
                                {preciosPrestadorUsandoFallback && (
                                    <div className="mb-3 p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                                        <p className="font-medium">No se pudieron cargar los precios desde el servidor. Se muestran valores de referencia.</p>
                                        <button type="button" onClick={() => fetchPreciosMembresiaPrestador()} className="mt-1.5 text-[#f79865] font-semibold hover:underline">
                                            Reintentar
                                        </button>
                                    </div>
                                )}
                                <div className={isRegistroPagadoPrestador ? 'mb-5' : 'mb-3'}>
                                    <span className={`block font-semibold text-gray-700 mb-3 ${isRegistroPagadoPrestador ? 'text-sm' : 'text-xs'}`}>{isRegistroPagadoPrestador ? 'Elige tu plan Redplika Premium' : 'Selecciona tu plan de pago'}</span>
                                    <div className={`grid grid-cols-3 ${isRegistroPagadoPrestador ? 'gap-3' : 'gap-2'}`}>
                                        {['mensual', 'semestral', 'anual'].map((plan) => {
                                            const valor = preciosMembresiaPrestador[plan];
                                            const mensual = preciosMembresiaPrestador.mensual != null ? Number(preciosMembresiaPrestador.mensual) : 0;
                                            const label = plan === 'mensual' ? 'Mensual' : plan === 'semestral' ? 'Semestral' : 'Anual';
                                            const selected = planMembresiaPrestador === plan;
                                            let ahorroTexto = null;
                                            if (plan === 'semestral' && valor != null && mensual > 0) {
                                                const equiv = mensual * 6;
                                                const ahorro = equiv - Number(valor);
                                                if (ahorro > 0) {
                                                    const pct = Math.round((ahorro / equiv) * 100);
                                                    ahorroTexto = `Ahorras $${ahorro.toLocaleString('es-CO')} (${pct}%)`;
                                                }
                                            }
                                            if (plan === 'anual' && valor != null && mensual > 0) {
                                                const equiv = mensual * 12;
                                                const ahorro = equiv - Number(valor);
                                                if (ahorro > 0) {
                                                    const pct = Math.round((ahorro / equiv) * 100);
                                                    ahorroTexto = `Ahorras $${ahorro.toLocaleString('es-CO')} (${pct}%)`;
                                                }
                                            }
                                            return (
                                                <button
                                                    key={plan}
                                                    type="button"
                                                    onClick={() => setPlanMembresiaPrestador(plan)}
                                                    className={`rounded-xl border-2 text-center transition-all duration-200 ${
                                                        isRegistroPagadoPrestador
                                                            ? `px-3 py-4 shadow-sm ${selected ? 'border-[#f79865] bg-[#fff5f0] text-[#c45c2a] font-semibold shadow-md ring-2 ring-[#f79865]/30' : 'border-gray-200 bg-white text-gray-600 hover:border-[#f79865]/60 hover:shadow-md'}`
                                                            : selected ? 'border-[#f79865] bg-[#fff5f0] text-[#f79865] font-semibold px-2 py-3' : 'border-gray-200 bg-white text-gray-600 hover:border-[#f79865]/50 px-2 py-3'
                                                    }`}
                                                >
                                                    <span className={`block font-medium ${isRegistroPagadoPrestador ? 'text-sm' : 'text-xs'}`}>{label}</span>
                                                    <span className={`block font-bold mt-1 ${isRegistroPagadoPrestador ? 'text-base' : 'text-sm'}`}>
                                                        {valor != null ? `$${Number(valor).toLocaleString('es-CO')}` : '—'}
                                                    </span>
                                                    {ahorroTexto && (
                                                        <span className={`block font-medium text-green-600 leading-tight mt-1.5 ${isRegistroPagadoPrestador ? 'text-xs' : 'text-[10px]'}`}>
                                                            {ahorroTexto}
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <BotonSubmitEpayco
                                    prominent
                                    descripcion={isRegistroPagadoPrestador ? `Membresía Premium (${planMembresiaPrestador})` : `Pago Membresía Premium (${planMembresiaPrestador})`}
                                    correo={data?.email}
                                    amount={preciosMembresiaPrestador[planMembresiaPrestador]}
                                    invoice={data?.email}
                                    disabled={error || preciosMembresiaPrestador[planMembresiaPrestador] == null || !data?.aceptoAutorizacion}
                                    disabledFunction={validateAllUniqueFields}
                                    redireccion={APP_URLS.registroAfiliadoEpayco}
                                    doctorId={undefined}
                                    registrationData={data}
                                    extraData={{
                                        tipo: 'afiliado',
                                        fullName: data?.fullName,
                                        idNumber: data?.idNumber,
                                        email: data?.email,
                                        cellNumber: data?.cellNumber,
                                        service: data?.service,
                                        x_extra1: 'membresia',
                                        x_extra2: data?.idNumber || '',
                                        x_extra3: data?.fullName || '',
                                        x_extra4: data?.cellNumber || '',
                                        x_extra5: data?.service || '',
                                        x_extra6: data?.email || '',
                                        x_extra7: planMembresiaPrestador || 'mensual'
                                    }}
                                />
                            </>
                        )}

                        <div className="flex justify-center mt-2 mb-2">
                            <button
                                type="button"
                                className="text-[#1f94c2ff] text-sm hover:underline"
                                onClick={() => navigate('/login')}
                            >
                                Ir a Iniciar Sesión
                            </button>
                        </div>
                    </form>
                        )}
                    </>
                )}

                </div>
            </div>

            {/* Modal de autorización antes de OAuth */}
            <Modal
                isOpen={showAuthModal}
                onRequestClose={() => {
                    setShowAuthModal(false);
                    setPendingOAuthProvider(null);
                    setAceptoAutorizacionOAuth(false);
                }}
                className="w-full max-w-[420px] mx-auto bg-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-2xl text-center border-2 border-[#6e787aff] focus:outline-none max-h-[90vh] overflow-y-auto"
                overlayClassName="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm z-50 p-4"
            >
                <h2 className="text-xl sm:text-2xl font-black text-[#1f94c2ff] mb-3 sm:mb-4">
                    Autorización Requerida
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 px-2">
                    Para continuar con el registro mediante {pendingOAuthProvider === 'google' ? 'Google' : 'LinkedIn'}, 
                    debes aceptar la autorización para el tratamiento de datos personales.
                </p>

                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 border-2 border-[#1f94c2ff] rounded-lg">
                    <div className="flex items-start">
                        <input
                            type="checkbox"
                            id="aceptoAutorizacionOAuth"
                            checked={aceptoAutorizacionOAuth}
                            onChange={(e) => setAceptoAutorizacionOAuth(e.target.checked)}
                            className="mt-0.5 sm:mt-1 mr-2 sm:mr-3 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
                        />
                        <label htmlFor="aceptoAutorizacionOAuth" className="text-xs sm:text-sm text-[#1f94c2ff] font-medium leading-tight">
                            He leído y acepto la {' '}
                            <a 
                                href={`${window.location.origin}/autorizaciondatos`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#1f94c2ff] hover:underline font-semibold break-words"
                            >
                                Autorización para tratamiento de datos personales
                            </a>.
                        </label>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:space-x-3">
                    <button
                        onClick={() => {
                            setShowAuthModal(false);
                            setPendingOAuthProvider(null);
                            setAceptoAutorizacionOAuth(false);
                        }}
                        className="w-full sm:flex-1 px-4 py-2.5 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-300 font-medium text-sm sm:text-base"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleConfirmOAuth}
                        disabled={!aceptoAutorizacionOAuth}
                        className={`w-full sm:flex-1 px-4 py-2.5 sm:py-2 rounded-lg transition duration-300 font-medium text-sm sm:text-base ${
                            aceptoAutorizacionOAuth
                                ? 'bg-[#1f94c2ff] text-white hover:bg-[#3090b3ff]'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        Continuar con {pendingOAuthProvider === 'google' ? 'Google' : 'LinkedIn'}
                    </button>
                </div>
            </Modal>

            {/* Modal de selección de especialidad para prestador (aparece después de elegir Google/LinkedIn) */}
            <Modal
                isOpen={showServiceModalPrestador}
                onRequestClose={() => {
                    setShowServiceModalPrestador(false);
                    setPendingOAuthProvider(null);
                }}
                className="w-full max-w-[380px] mx-auto bg-white rounded-xl shadow-xl border border-gray-300 focus:outline-none max-h-[70vh] overflow-hidden flex flex-col"
                overlayClassName="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-[100] p-4"
            >
                <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-base font-bold text-[#1f94c2ff]">
                            Elige tu especialidad
                        </h2>
                        <button
                            onClick={() => {
                                setShowServiceModalPrestador(false);
                                setPendingOAuthProvider(null);
                            }}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label="Cerrar"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Continuarás con {pendingOAuthProvider === 'google' ? 'Google' : 'LinkedIn'}
                    </p>
                </div>
                <div className="flex-1 overflow-y-auto px-3 py-3">
                    {Object.entries(especialidadesPrestador).map(([groupName, services]) => (
                        <div key={groupName} className="mb-3">
                            <h3 className="text-xs font-semibold text-gray-600 mb-2">{toSentenceCase(groupName)}</h3>
                            <div className="space-y-1.5">
                                {services.map((service) => (
                                    <button
                                        key={service.value}
                                        type="button"
                                        onClick={() => handlePrestadorSpecialtySelected(service.value)}
                                        className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:border-[#1f94c2ff] hover:bg-blue-50 transition-all duration-200 text-sm"
                                    >
                                        {service.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Modal>
        </div>
    )
}

const RegistroPaciente = ({ setData, setError, initialEmail = '', fromOAuthPaidUser = false, formVariant }) => {
    const location = useLocation();
    const isPremiumForm = formVariant === 'premium';
    const isGratuitoForm = formVariant === 'gratuito';
    const isStyledForm = isPremiumForm || isGratuitoForm;
    const focusRing = isPremiumForm ? 'focus:ring-[#f79865]/25 focus:border-[#f79865]' : isGratuitoForm ? 'focus:ring-[#1f94c2]/25 focus:border-[#1f94c2]' : 'focus:ring-1 focus:ring-[#1f94c2ff]';
    const inputClassPaciente = isStyledForm
        ? `w-full px-3 py-2.5 text-sm rounded-xl bg-white border border-gray-200 focus:outline-none focus:ring-2 ${focusRing} transition-shadow`
        : 'w-full px-2 py-1 text-sm bg-white border border-[#6e787aff] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1f94c2ff]';
    const labelClassPaciente = isStyledForm ? 'block text-gray-600 text-xs font-medium ml-1 mb-1' : 'block text-[#70706eff] text-xs font-medium ml-1 mb-0.5';
    const emailFromOAuth = location.state?.emailFromOAuth || location.state?.patientEmail || initialEmail;
    // Si viene de OAuth y ya aceptó la autorización, marcarla como aceptada
    const aceptoAutorizacionFromOAuth = location.state?.aceptoAutorizacion || false;

    const [formData, setFormData] = useState({
        firstName: '',
        secondName: '',
        firstLastName: '',
        secondLastName: '',
        email: emailFromOAuth || '',
        cellNumber: '',
        password: '',
        confirmPassword: '',
    });

    const [aceptoAutorizacion, setAceptoAutorizacion] = useState(aceptoAutorizacionFromOAuth);
    const [touched, setTouched] = useState({
      aceptoAutorizacion: false,
      password: false,
      confirmPassword: false
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordRequirements, setPasswordRequirements] = useState({
        minLength: false,
        hasUpperCase: false,
        hasNumber: false,
        hasSymbol: false
    });

    // Validación de contraseña en tiempo real (sin setState durante render)
    const validatePassword = (password) => {
        const requirements = {
            minLength: password.length >= 8,
            hasUpperCase: /[A-Z]/.test(password),
            hasNumber: /\d/.test(password),
            hasSymbol: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };
        return {
            requirements,
            isValid: Object.values(requirements).every(req => req === true)
        };
    };

    // Calcular validaciones sin llamar setState durante el render
    const passwordValidation = formData.password ? validatePassword(formData.password) : { requirements: { minLength: false, hasUpperCase: false, hasNumber: false, hasSymbol: false }, isValid: false };
    const isPasswordValid = passwordValidation.isValid;
    const passwordsMatch = formData.password === formData.confirmPassword && formData.confirmPassword !== '';

    const validate = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const hasRequiredFields = Boolean(
            formData.firstName?.trim() &&
            formData.firstLastName?.trim() &&
            formData.email?.trim() &&
            emailRegex.test(formData.email.trim()) &&
            formData.cellNumber?.trim()?.length >= 10 &&
            aceptoAutorizacion
        );

        if (fromOAuthPaidUser) {
            return hasRequiredFields;
        }

        return hasRequiredFields && isPasswordValid && passwordsMatch;
    };

    const validarTipoExpresionRegular = (e) => {
        if (e.target.name.includes('Name')) {
            return validarInputExpresionRegular(e, /[^a-zA-ZñÑ\s]/g);
        } else if (e.target.name === 'email') {
            return e.target.value;
        } else if (e.target.name === 'cellNumber') {
            return validarInputExpresionRegular(e, /[^0-9]/g);
        } else if (e.target.name === 'password' || e.target.name === 'confirmPassword') {
            return e.target.value;
        }
    }

    const onHandleChange = (e) => {
        const value = validarTipoExpresionRegular(e);
        setFormData({
            ...formData,
            [e.target.name]: value,
        });
        
        // Marcar como touched cuando el usuario empieza a escribir
        if (e.target.name === 'password' || e.target.name === 'confirmPassword') {
            setTouched(prev => ({ ...prev, [e.target.name]: true }));
        }
    }

    const handleCheckboxChange = (e) => {
        setAceptoAutorizacion(e.target.checked);
        setTouched(prev => ({ ...prev, aceptoAutorizacion: true }));
    };

    useEffect(() => {
        // Validar contraseña en tiempo real y actualizar requirements
        if (formData.password) {
            const validation = validatePassword(formData.password);
            setPasswordRequirements(validation.requirements);
        } else {
            setPasswordRequirements({
                minLength: false,
                hasUpperCase: false,
                hasNumber: false,
                hasSymbol: false
            });
        }
    }, [formData.password]); // Solo dependemos de formData.password, no de todo formData

    useEffect(() => {
        setData({ ...formData, aceptoAutorizacion });
        if (setError) {
            setError(!validate());
        }
    }, [formData, aceptoAutorizacion, fromOAuthPaidUser, setError]);

    // Efecto para hacer scroll hacia la casilla de autorización cuando viene de OAuth
    useEffect(() => {
        if (emailFromOAuth) {
            // Múltiples intentos para asegurar que el DOM esté renderizado
            const scrollToCheckbox = () => {
                const checkbox = document.getElementById('aceptoAutorizacionUsuario');
                if (checkbox) {
                    checkbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // También enfocar el checkbox para que sea más visible
                    setTimeout(() => checkbox.focus(), 100);
                    return true;
                }
                return false;
            };
            
            // Intentar inmediatamente
            if (!scrollToCheckbox()) {
                // Si no funciona, intentar después de un delay
                setTimeout(() => {
                    if (!scrollToCheckbox()) {
                        // Último intento después de más tiempo
                        setTimeout(scrollToCheckbox, 500);
                    }
                }, 300);
            }
        }
    }, [emailFromOAuth]);

    return (
        <>
            {/* Nombres en dos columnas */}
            <div className={`grid grid-cols-2 gap-2 ${isStyledForm ? 'mb-3' : 'mb-1.5'}`}>
                <div className="relative">
                    <label htmlFor="firstName" className={labelClassPaciente}>Primer Nombre *</label>
                    <input type="text" id="firstName" name="firstName" placeholder="Primer Nombre" value={formData.firstName} required onInput={onHandleChange} className={inputClassPaciente} />
                </div>
                <div className="relative">
                    <label htmlFor="secondName" className={labelClassPaciente}>Segundo Nombre</label>
                    <input type="text" id="secondName" name="secondName" placeholder="Segundo Nombre" value={formData.secondName} onInput={onHandleChange} className={inputClassPaciente} />
                </div>
            </div>
            <div className={`grid grid-cols-2 gap-2 ${isStyledForm ? 'mb-3' : 'mb-1.5'}`}>
                <div className="relative">
                    <label htmlFor="firstLastName" className={labelClassPaciente}>Primer Apellido *</label>
                    <input type="text" id="firstLastName" name="firstLastName" placeholder="Primer Apellido" value={formData.firstLastName} onInput={onHandleChange} required className={inputClassPaciente} />
                </div>
                <div className="relative">
                    <label htmlFor="secondLastName" className={labelClassPaciente}>Segundo Apellido</label>
                    <input type="text" id="secondLastName" name="secondLastName" placeholder="Segundo Apellido" value={formData.secondLastName} onInput={onHandleChange} className={inputClassPaciente} />
                </div>
            </div>
            <div className={`grid grid-cols-2 gap-2 ${isStyledForm ? 'mb-3' : 'mb-1.5'}`}>
                <div className="relative">
                    <label htmlFor="email" className={labelClassPaciente}>Correo *</label>
                    <input type="text" id="email" name="email" placeholder="correo@ejemplo.com" value={formData.email} onInput={onHandleChange} required className={inputClassPaciente} />
                </div>
                <div className="relative">
                    <label htmlFor="cellNumber" className={labelClassPaciente}>Celular *</label>
                    <input type="text" id="cellNumber" name="cellNumber" maxLength={10} placeholder="3212345678" value={formData.cellNumber} onInput={onHandleChange} required className={inputClassPaciente} />
                </div>
            </div>
            
            {/* Campos de contraseña (ocultos cuando viene de OAuth pagado: ya está autenticado) */}
            {!fromOAuthPaidUser && (
                <>
            <div className={isStyledForm ? 'mb-3 relative' : 'mb-1.5 relative'}>
                <label htmlFor="password" className={labelClassPaciente}>Contraseña *</label>
                <div className="flex items-center relative">
                    <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        placeholder="Crea una contraseña segura"
                        value={formData.password}
                        onChange={onHandleChange}
                        onFocus={() => setTouched(prev => ({ ...prev, password: true }))}
                        required
                        className={`${isStyledForm ? `w-full px-3 py-2.5 text-sm rounded-xl bg-white border pr-10 focus:outline-none focus:ring-2 ${focusRing} transition-shadow` : 'w-full px-2 py-1 text-sm bg-white border rounded-lg focus:outline-none pr-8'} ${
                            touched.password && !isPasswordValid ? 'border-red-500' : touched.password && isPasswordValid ? 'border-green-500' : isStyledForm ? 'border-gray-200' : 'border-[#6e787aff]'
                        }`}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 text-[#70706eff] hover:text-[#1f94c2ff]"
                    >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                </div>
                
                {touched.password && (
                    <div className="mt-1 ml-1 text-xs grid grid-cols-2 gap-x-2 gap-y-0.5">
                        <div className={`flex items-center ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                            <span className="mr-1 text-[10px]">{passwordRequirements.minLength ? '✓' : '○'}</span>
                            <span className="text-[10px]">8+ caracteres</span>
                        </div>
                        <div className={`flex items-center ${passwordRequirements.hasUpperCase ? 'text-green-600' : 'text-gray-500'}`}>
                            <span className="mr-1 text-[10px]">{passwordRequirements.hasUpperCase ? '✓' : '○'}</span>
                            <span className="text-[10px]">Mayúscula</span>
                        </div>
                        <div className={`flex items-center ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                            <span className="mr-1 text-[10px]">{passwordRequirements.hasNumber ? '✓' : '○'}</span>
                            <span className="text-[10px]">Número</span>
                        </div>
                        <div className={`flex items-center ${passwordRequirements.hasSymbol ? 'text-green-600' : 'text-gray-500'}`}>
                            <span className="mr-1 text-[10px]">{passwordRequirements.hasSymbol ? '✓' : '○'}</span>
                            <span className="text-[10px]">Símbolo</span>
                        </div>
                    </div>
                )}
            </div>

            <div className={isStyledForm ? 'mb-3 relative' : 'mb-2 relative'}>
                <label htmlFor="confirmPassword" className={labelClassPaciente}>Confirmar Contraseña *</label>
                <div className="flex items-center relative">
                    <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        placeholder="Confirma tu contraseña"
                        value={formData.confirmPassword}
                        onChange={onHandleChange}
                        onFocus={() => setTouched(prev => ({ ...prev, confirmPassword: true }))}
                        required
                        className={`${isStyledForm ? `w-full px-3 py-2.5 text-sm rounded-xl bg-white border pr-10 focus:outline-none focus:ring-2 ${focusRing} transition-shadow` : 'w-full px-2 py-1 text-sm bg-white border rounded-lg focus:outline-none pr-8'} ${
                            touched.confirmPassword && !passwordsMatch ? 'border-red-500' : touched.confirmPassword && passwordsMatch ? 'border-green-500' : isStyledForm ? 'border-gray-200' : 'border-[#6e787aff]'
                        }`}
                    />
                    <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-2 text-[#70706eff] hover:text-[#1f94c2ff]"
                    >
                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                </div>
                {touched.confirmPassword && !passwordsMatch && (
                    <span className="text-red-500 text-xs ml-1 block">Las contraseñas no coinciden</span>
                )}
                {touched.confirmPassword && passwordsMatch && (
                    <span className="text-green-600 text-xs ml-1 block">✓ Las contraseñas coinciden</span>
                )}
            </div>
                </>
            )}

            <div className={`mb-2 relative ${emailFromOAuth ? 'border-2 border-[#1f94c2ff] rounded-lg p-3 bg-blue-50' : ''}`}>
                {emailFromOAuth && (
                    <p className="text-xs font-semibold text-[#1f94c2ff] mb-2">⚠️ Campo obligatorio</p>
                )}
                <div className="flex items-start">
                    <input
                        type="checkbox"
                        id="aceptoAutorizacionUsuario"
                        name="aceptoAutorizacionUsuario"
                        checked={aceptoAutorizacion}
                        onChange={handleCheckboxChange}
                        required
                        className={`mt-0.5 mr-2 ${emailFromOAuth ? 'w-5 h-5' : ''}`}
                    />
                    <label htmlFor="aceptoAutorizacionUsuario" className={`text-xs leading-tight ${emailFromOAuth ? 'text-[#1f94c2ff] font-medium' : 'text-[#70706eff]'}`}>
                        He leído y acepto la {' '}
                        <a 
                            href={`${window.location.origin}/autorizaciondatos`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[#1f94c2ff] hover:underline font-semibold"
                        >
                            Autorización para tratamiento de datos personales
                        </a>.
                    </label>
                </div>
                {touched.aceptoAutorizacion && !aceptoAutorizacion && (
                    <span className="text-red-500 text-xs block ml-5 mt-0.5">Debes aceptar la autorización.</span>
                )}
            </div>
        </>
    )
}

const RegistroAfiliado = ({ preInfo, setData, setError, isPagado = false, formVariant }) => {
    const isPremiumForm = formVariant === 'premium';
    const isGratuitoForm = formVariant === 'gratuito';
    const isStyledForm = isPremiumForm || isGratuitoForm;
    const focusRing = isPremiumForm ? 'focus:ring-[#f79865]/25 focus:border-[#f79865]' : isGratuitoForm ? 'focus:ring-[#1f94c2]/25 focus:border-[#1f94c2]' : '';
    const inputClass = isStyledForm
        ? `w-full px-3 py-2.5 rounded-xl bg-white border border-gray-200 focus:outline-none focus:ring-2 ${focusRing} transition-shadow`
        : 'w-full px-2 py-1 rounded-lg bg-white border border-[#6e787aff] focus:outline-none';
    const labelClass = isStyledForm ? 'block text-gray-600 ml-1 font-medium text-sm mb-1' : 'block text-[#70706eff] ml-3 font-medium';
    const fieldSpacing = isStyledForm ? 'mb-3 relative' : 'mb-2 relative';
    const location = useLocation();
    const emailFromOAuth = location.state?.emailFromOAuth || '';
    
    const [formData, setFormData] = useState({
        fullName: preInfo?.fullName || '',
        idNumber: preInfo?.idNumber || '',
        email: preInfo?.email || emailFromOAuth || '',
        cellNumber: preInfo?.cellNumber || '',
        service: preInfo?.service || '',
        password: '',
        confirmPassword: '',
    });

    const [aceptoAutorizacion, setAceptoAutorizacion] = useState(preInfo?.aceptoAutorizacion === true);
    const [touched, setTouched] = useState({
      aceptoAutorizacion: preInfo?.aceptoAutorizacion === true,
      password: false,
      confirmPassword: false
    });

    const [inputsSelect, setInputsSelect] = useState({});
    const [errors, setErrors] = useState({});
    const [isSomeError, setIsSomeError] = useState(false);
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [especialidades, setEspecialidades] = useState(FALLBACK_PROFESSIONS_GROUPED);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordRequirements, setPasswordRequirements] = useState({
        minLength: false,
        hasUpperCase: false,
        hasNumber: false,
        hasSymbol: false
    });

    const validatePassword = (password) => {
        const requirements = {
            minLength: password.length >= 8,
            hasUpperCase: /[A-Z]/.test(password),
            hasNumber: /\d/.test(password),
            hasSymbol: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };
        return { requirements, isValid: Object.values(requirements).every(r => r === true) };
    };

    const validarTipoExpresionRegular = (e) => {
        switch (e.target.name) {
            case 'fullName':
                return e.target.value.replace(/[^a-zA-ZñÑ\s]/g, '');
            case 'idNumber':
                return e.target.value.replace(/[^0-9]/g, '');
            case 'email':
                return e.target.value;
            case 'cellNumber':
                return e.target.value.replace(/[^0-9]/g, '');
            case 'password':
            case 'confirmPassword':
                return e.target.value;
            default:
                return e.target.value;
        }
    }

    const validate = () => {
        let newErrors = {};

        if (!formData.fullName.trim()) {
            newErrors.fullName = 'El nombre completo es obligatorio.';
        } else if (formData.fullName.length < 8 || formData.fullName.length > 50) {
            newErrors.fullName = 'El nombre debe tener entre 8 y 50 caracteres.';
        }

        if (!formData.idNumber.trim()) {
            newErrors.idNumber = 'La cédula es obligatoria.';
        } else if (formData.idNumber.length < 7 || formData.idNumber.length > 10) {
            newErrors.idNumber = 'La cédula debe tener entre 7 y 10 caracteres.';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'El correo es obligatorio.';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'El correo no es válido.';
        }

        if (!formData.cellNumber.trim()) {
            newErrors.cellNumber = 'El celular es obligatorio.';
        } else if (!formData.cellNumber.startsWith('3')) {
            newErrors.cellNumber = 'El celular debe iniciar con 3.';
        } else if (formData.cellNumber.length !== 10) {
            newErrors.cellNumber = 'El celular debe tener 10 caracteres.';
        }

        if (!formData.service.trim()) {
            newErrors.service = 'El servicio es obligatorio.';
        }

        if (!formData.password) {
            newErrors.password = 'La contraseña es obligatoria.';
        } else {
            const { isValid } = validatePassword(formData.password);
            if (!isValid) {
                newErrors.password = 'La contraseña debe tener 8+ caracteres, mayúscula, número y símbolo.';
            }
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Confirma tu contraseña.';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Las contraseñas no coinciden.';
        }
        
        if (touched.aceptoAutorizacion && !aceptoAutorizacion) {
            newErrors.aceptoAutorizacion = 'Debes aceptar la autorización para el tratamiento de datos personales.';
        }

        setErrors(newErrors);
        setIsSomeError(Object.keys(newErrors).length > 0);
        return Object.keys(newErrors).length === 0;
    }

    const onHandleChange = (e) => {
        setInputsSelect({ ...inputsSelect, [e.target.name]: { name: e.target.name, state: true } });
        const value = validarTipoExpresionRegular(e);
        setFormData({
            ...formData,
            [e.target.name]: value,
        });
        if (e.target.name === 'password' || e.target.name === 'confirmPassword') {
            setTouched(prev => ({ ...prev, [e.target.name]: true }));
        }
    }

    const handleServiceSelect = (serviceValue, serviceLabel) => {
        setFormData({
            ...formData,
            service: serviceValue,
        });
        setInputsSelect({ ...inputsSelect, service: { name: 'service', state: true } });
        setShowServiceModal(false);
    }

    useEffect(() => {
        fetchProfessionsGrouped().then(setEspecialidades);
    }, []);

    const getServiceLabel = (value) => getServiceLabelFromGrouped(especialidades, value);

    const handleCheckboxChange = (e) => {
        setAceptoAutorizacion(e.target.checked);
        setTouched(prev => ({ ...prev, aceptoAutorizacion: true }));
    };

    useEffect(() => {
        if (formData.password) {
            const { requirements } = validatePassword(formData.password);
            setPasswordRequirements(requirements);
        } else {
            setPasswordRequirements({ minLength: false, hasUpperCase: false, hasNumber: false, hasSymbol: false });
        }
    }, [formData.password]);

    //Cada que cambie el formulario o la casilla lo validamos
    useEffect(() => {
        const isValid = validate();
        const dataToSet = { 
            ...formData, 
            aceptoAutorizacion 
        };
        setData(dataToSet);
        setError(!isValid);
    }, [formData, aceptoAutorizacion]);

    // Efecto para hacer scroll hacia la casilla de autorización cuando viene de OAuth
    useEffect(() => {
        if (emailFromOAuth) {
            // Múltiples intentos para asegurar que el DOM esté renderizado
            const scrollToCheckbox = () => {
                const checkbox = document.getElementById('aceptoAutorizacionAfiliado');
                if (checkbox) {
                    checkbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // También enfocar el checkbox para que sea más visible
                    setTimeout(() => checkbox.focus(), 100);
                    return true;
                }
                return false;
            };
            
            // Intentar inmediatamente
            if (!scrollToCheckbox()) {
                // Si no funciona, intentar después de un delay
                setTimeout(() => {
                    if (!scrollToCheckbox()) {
                        // Último intento después de más tiempo
                        setTimeout(scrollToCheckbox, 500);
                    }
                }, 300);
            }
        }
    }, [emailFromOAuth]);

    return (
        <>
            <div className={fieldSpacing}>
                <label htmlFor="fullName" className={labelClass}>Nombre completo *</label>
                <div className="flex items-center">
                    <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        placeholder="Tu Nombre Completo"
                        value={formData.fullName}
                        required 
                        onInput={onHandleChange}
                        className={inputClass}
                    />
                </div>
                {inputsSelect['fullName']?.state && errors.fullName && <span className="text-red-500 text-sm">{errors.fullName}</span>}
            </div>
            <div className={fieldSpacing}>
                <label htmlFor="idNumber" className={labelClass}>Cédula *</label>
                <div className="flex items-center">
                    <input
                        type="text"
                        id="idNumber"
                        name="idNumber"
                        maxLength={10}
                        placeholder="123498765"
                        value={formData.idNumber}
                        onInput={onHandleChange}
                        required
                        className={inputClass}
                    />
                </div>
                {inputsSelect['idNumber']?.state && errors.idNumber && <span className="text-red-500 text-sm">{errors.idNumber}</span>}
            </div>
            <div className={fieldSpacing}>
                <label htmlFor="email" className={labelClass}>Correo *</label>
                <div className="flex items-center">
                    <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="tucorreo@ejemplo.com"
                        value={formData.email}
                        onInput={onHandleChange}
                        required
                        className={inputClass}
                    />
                </div>
                {inputsSelect['email']?.state && errors.email && <span className="text-red-500 text-sm">{errors.email}</span>}
            </div>
            <div className={fieldSpacing}>
                <label htmlFor="cellNumber" className={labelClass}>Celular *</label>
                <div className="flex items-center">
                    <input
                        type="text"
                        id="cellNumber"
                        name="cellNumber"
                        maxLength={10}
                        placeholder="3212345678"
                        value={formData.cellNumber}
                        onInput={onHandleChange}
                        required
                        className={inputClass}
                    />
                </div>
                {inputsSelect['cellNumber']?.state && errors.cellNumber && <span className="text-red-500 text-sm">{errors.cellNumber}</span>}
            </div>

            <div className={fieldSpacing}>
                <label htmlFor="passwordAfiliado" className={`${labelClass} mb-0.5 text-sm`}>Contraseña *</label>
                <div className="flex items-center relative">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        id="passwordAfiliado"
                        name="password"
                        placeholder="Mín. 8 caracteres, mayúscula, número y símbolo"
                        value={formData.password}
                        onChange={onHandleChange}
                        onFocus={() => setTouched(prev => ({ ...prev, password: true }))}
                        required
                        className={`${isStyledForm ? `w-full px-3 py-2.5 text-sm rounded-xl bg-white border pr-10 focus:outline-none focus:ring-2 ${focusRing} transition-shadow` : 'w-full px-2 py-1 text-sm bg-white border rounded-lg focus:outline-none pr-8'} ${
                            touched.password && (formData.password && !validatePassword(formData.password).isValid)
                                ? 'border-red-500'
                                : touched.password && formData.password && validatePassword(formData.password).isValid
                                ? 'border-green-500'
                                : isStyledForm ? `border-gray-200 ${isPremiumForm ? 'focus:border-[#f79865]' : 'focus:border-[#1f94c2]'}` : 'border-[#6e787aff]'
                        }`}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 text-[#70706eff] hover:text-[#1f94c2ff]"
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                </div>
                {touched.password && (
                    <div className="mt-1 ml-1 text-xs grid grid-cols-2 gap-x-2 gap-y-0.5">
                        <div className={`flex items-center ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                            <span className="mr-1 text-[10px]">{passwordRequirements.minLength ? '✓' : '○'}</span>
                            <span className="text-[10px]">8+ caracteres</span>
                        </div>
                        <div className={`flex items-center ${passwordRequirements.hasUpperCase ? 'text-green-600' : 'text-gray-500'}`}>
                            <span className="mr-1 text-[10px]">{passwordRequirements.hasUpperCase ? '✓' : '○'}</span>
                            <span className="text-[10px]">Mayúscula</span>
                        </div>
                        <div className={`flex items-center ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                            <span className="mr-1 text-[10px]">{passwordRequirements.hasNumber ? '✓' : '○'}</span>
                            <span className="text-[10px]">Número</span>
                        </div>
                        <div className={`flex items-center ${passwordRequirements.hasSymbol ? 'text-green-600' : 'text-gray-500'}`}>
                            <span className="mr-1 text-[10px]">{passwordRequirements.hasSymbol ? '✓' : '○'}</span>
                            <span className="text-[10px]">Símbolo</span>
                        </div>
                    </div>
                )}
                {inputsSelect['password']?.state && errors.password && <span className="text-red-500 text-sm">{errors.password}</span>}
            </div>

            <div className={fieldSpacing}>
                <label htmlFor="confirmPasswordAfiliado" className={`${labelClass} text-xs mb-0.5`}>Confirmar contraseña *</label>
                <div className="flex items-center relative">
                    <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPasswordAfiliado"
                        name="confirmPassword"
                        placeholder="Repite tu contraseña"
                        value={formData.confirmPassword}
                        onChange={onHandleChange}
                        onFocus={() => setTouched(prev => ({ ...prev, confirmPassword: true }))}
                        required
                        className={`${isStyledForm ? `w-full px-3 py-2.5 text-sm rounded-xl bg-white border pr-10 focus:outline-none focus:ring-2 ${focusRing} transition-shadow` : 'w-full px-2 py-1 text-sm bg-white border rounded-lg focus:outline-none pr-8'} ${
                            touched.confirmPassword && formData.confirmPassword && formData.password !== formData.confirmPassword
                                ? 'border-red-500'
                                : touched.confirmPassword && formData.password === formData.confirmPassword && formData.confirmPassword
                                ? 'border-green-500'
                                : isStyledForm ? `border-gray-200 ${isPremiumForm ? 'focus:border-[#f79865]' : 'focus:border-[#1f94c2]'}` : 'border-[#6e787aff]'
                        }`}
                    />
                    <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-2 text-[#70706eff] hover:text-[#1f94c2ff]"
                        aria-label={showConfirmPassword ? 'Ocultar' : 'Mostrar'}
                    >
                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                </div>
                {inputsSelect['confirmPassword']?.state && errors.confirmPassword && (
                    <span className="text-red-500 text-sm ml-1 block mt-0.5">{errors.confirmPassword}</span>
                )}
            </div>

            <div className={isStyledForm ? 'mb-5 relative' : 'mb-4 relative'}>
                <label htmlFor="service" className={`${labelClass} ml-1 mb-2 text-sm sm:text-base`}>
                    <span className="flex items-center">
                        <span className="mr-2">🏥</span>
                        Elige la especialidad *
                    </span>
                </label>
                {/* Input hidden para validación del formulario */}
                <input
                    type="hidden"
                    id="service"
                    name="service"
                    value={formData.service}
                    required
                />
                <button
                    type="button"
                    onClick={() => setShowServiceModal(true)}
                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl bg-white border-2 transition-all duration-300 cursor-pointer flex items-center justify-between
                        ${formData.service 
                            ? 'border-[#1f94c2ff] text-gray-800' 
                            : 'border-gray-300 text-gray-500'
                        }
                        ${inputsSelect['service']?.state && errors.service 
                            ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                            : 'focus:border-[#1f94c2ff] focus:ring-2 focus:ring-blue-100'
                        }
                        hover:border-[#1f94c2ff] hover:shadow-md
                    `}
                >
                    <span className={formData.service ? 'text-gray-800 font-medium' : 'text-gray-500'}>
                        {formData.service ? getServiceLabel(formData.service) : 'Elige una especialidad'}
                    </span>
                    <svg 
                        className={`w-5 h-5 transition-transform duration-300 ${formData.service ? 'text-[#1f94c2ff]' : 'text-gray-400'}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
                {inputsSelect['service']?.state && errors.service && (
                    <span className="text-red-500 text-sm mt-1 ml-3 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {errors.service}
                    </span>
                )}
                {formData.service && !errors.service && (
                    <span className="text-green-600 text-sm mt-1 ml-3 flex items-center">
                        <span className="mr-1">✓</span>
                        Especialidad seleccionada
                    </span>
                )}
            </div>

            {/* Modal de selección de especialidad */}
            <Modal
                isOpen={showServiceModal}
                onRequestClose={() => setShowServiceModal(false)}
                className="w-full max-w-[380px] mx-auto bg-white rounded-xl shadow-xl border border-gray-300 focus:outline-none max-h-[70vh] overflow-hidden flex flex-col"
                overlayClassName="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-[100] p-4"
            >
                <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-base font-bold text-[#1f94c2ff]">
                            Elige tu especialidad
                        </h2>
                        <button
                            onClick={() => setShowServiceModal(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label="Cerrar"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
                
                <div className="flex-1 overflow-y-auto px-3 py-3">
                    {Object.entries(especialidades).map(([groupName, services]) => (
                        <div key={groupName} className="mb-3">
                            <h3 className="text-xs font-semibold text-gray-600 mb-2">
                                {toSentenceCase(groupName)}
                            </h3>
                            <div className="space-y-1.5">
                                {services.map((service) => (
                                    <button
                                        key={service.value}
                                        type="button"
                                        onClick={() => handleServiceSelect(service.value, service.label)}
                                        className={`w-full text-left px-3 py-2 rounded-lg border transition-all duration-200 text-sm
                                            ${formData.service === service.value
                                                ? 'bg-[#1f94c2ff] text-white border-[#1f94c2ff]'
                                                : 'bg-white text-gray-700 border-gray-200 hover:border-[#1f94c2ff] hover:bg-blue-50'
                                            }
                                        `}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className={formData.service === service.value ? 'text-white' : 'text-gray-800'}>
                                                {service.label}
                                            </span>
                                            {formData.service === service.value && (
                                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Modal>
            <div className={`mb-4 relative ${emailFromOAuth ? 'border-2 border-[#1f94c2ff] rounded-lg p-3 bg-blue-50' : ''}`}>
                {emailFromOAuth && (
                    <p className="text-sm font-semibold text-[#1f94c2ff] mb-2">⚠️ Campo obligatorio</p>
                )}
                <div className="flex items-start">
                    <input
                        type="checkbox"
                        id="aceptoAutorizacionAfiliado"
                        name="aceptoAutorizacionAfiliado"
                        checked={aceptoAutorizacion}
                        onChange={handleCheckboxChange}
                        required
                        className={emailFromOAuth ? 'w-5 h-5 mt-0.5' : ''}
                    />
                    <label htmlFor="aceptoAutorizacionAfiliado" className={`ml-2 text-sm ${emailFromOAuth ? 'text-[#1f94c2ff] font-medium' : 'text-[#70706eff]'}`}>
                        He leído y acepto la {' '}
                        <a 
                            href={`${window.location.origin}/autorizaciondatosm`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={isPagado ? 'text-[#f79865] hover:underline font-semibold' : 'text-[#1f94c2ff] hover:underline font-semibold'}
                        >
                            Autorización para tratamiento de datos personales
                        </a>.
                    </label>
                </div>
                {touched.aceptoAutorizacion && !aceptoAutorizacion && (
                    <span className="text-red-500 text-sm block mt-1">Debes aceptar la autorización para el tratamiento de datos personales.</span>
                )}
            </div>
        </>
    );
}
//Fin
export default RegistroAfiliado;